import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { getAllVisaCountries } from "../visacountry/services/visacountry.services";
import { IVisaCountry } from "../visacountry/visacountryModel";
import { clearAdditionalServiceDetails } from "./additionalserviceSlice";
import {
  createAdditionalService,
  getAdditionalServiceByID,
  getAllAdditionalServices,
  updateAdditionalService,
} from "./services/additionalservice.services";
import FormTextArea from "../../components/formFields/FormTextArea";

const CreateAdditionalService: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const [formKey, setFormKey] = useState(1);

  const AdditionalServiceDetail = useAppSelector(
    (state) => state.additionalservice.AdditionalServiceDetail
  );
  const VisaCountryList = useAppSelector(
    (state) => state.visacountry.VisaCountryList
  );
  const AdditionalServiceID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector(
    (state) => state.additionalservice.formLoading
  );

  useEffect(() => {
    dispatch(getAllVisaCountries());
    return () => {
      dispatch(clearAdditionalServiceDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (AdditionalServiceID) {
      dispatch(getAdditionalServiceByID(AdditionalServiceID));
    }
  }, [AdditionalServiceID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [AdditionalServiceDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (!AdditionalServiceID) {
        const insertPayload = {
          VisaCountryID: values.VisaCountryID ? +values.VisaCountryID : null,
          ServiceName: values.ServiceName,
          ServiceDescription: values.ServiceDescription,
          Price: values.Price ? +values.Price : null,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createAdditionalService(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearAdditionalServiceDetails());
          dispatch(getAllAdditionalServices());
        }
      } else {
        const editPayload = {
          ID: AdditionalServiceID,
          VisaCountryID: values.VisaCountryID ? +values.VisaCountryID : null,
          ServiceName: values.ServiceName,
          ServiceDescription: values.ServiceDescription,
          Price: values.Price ? +values.Price : null,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateAdditionalService(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearAdditionalServiceDetails());
          dispatch(getAllAdditionalServices());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={AdditionalServiceDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {AdditionalServiceID
                    ? "Update Additional Service"
                    : "Add Additional Service"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"VisaCountryID"}
                  name={"VisaCountryID"}
                  label={"Visa Country"}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={VisaCountryList?.map((vcounry: IVisaCountry) => {
                    return {
                      value: vcounry.ID,
                      label: vcounry.VisaCountry,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="ServiceName"
                  label="Service Name"
                  placeholder="i.e. Pickup & Delivery"
                  component={FormTextField}
                  astrike={true}
                  maxLength={50}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="ServiceDescription"
                  label="Service Description"
                  component={FormTextArea}
                  astrike={true}
                  type={"text"}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="Price"
                  label="Price"
                  placeholder="i.e ₹2,500"
                  component={FormNumericTextField}
                  astrike={true}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  placeholder="i.e. Active"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: 20,
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={AdditionalServiceID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateAdditionalService;
